import { Article } from '@/app/core/models/article';
import { SupportedLang } from '@/app/core/models/supportedLang';

export interface ArticleJsonLdContext {
	lang: SupportedLang;
	siteUrl: string;
	/** URL absolue de la page de l'article. */
	pageUrl: string;
	/** URL absolue de l'image principale. */
	image?: string;
	/** Texte de l'article débarrassé de son HTML, pour `description`. */
	description: string;
}

/**
 * Balisage `NewsArticle` d'un article.
 *
 * `publisher` pointe vers le nœud `#organization` posé sur toutes les pages,
 * plutôt que de redéclarer l'éditeur : les moteurs recollent les deux.
 */
export function buildArticleJsonLd(article: Article, ctx: ArticleJsonLdContext): Record<string, unknown> | null {
	const title = article.translations?.[ctx.lang]?.title?.trim();
	if (!title) return null;

	const site = ctx.siteUrl.replace(/\/+$/, '');
	const published = article.createdAt ? new Date(article.createdAt) : null;
	const publishedIso = published && !Number.isNaN(published.getTime()) ? published.toISOString() : undefined;

	const jsonLd: Record<string, unknown> = {
		'@context': 'https://schema.org',
		'@type': 'NewsArticle',
		headline: title,
		description: ctx.description,
		mainEntityOfPage: { '@type': 'WebPage', '@id': ctx.pageUrl },
		url: ctx.pageUrl,
		inLanguage: ctx.lang === 'en' ? 'en-US' : 'fr-FR',
		author: { '@id': `${site}/#organization` },
		publisher: { '@id': `${site}/#organization` },
	};

	if (ctx.image) jsonLd['image'] = [ctx.image];
	if (publishedIso) {
		jsonLd['datePublished'] = publishedIso;
		jsonLd['dateModified'] = publishedIso;
	}

	return jsonLd;
}

/**
 * Fil d'Ariane d'une page de détail. Google l'utilise pour remplacer l'URL
 * brute par un chemin lisible sous le titre du résultat.
 */
export function buildBreadcrumbJsonLd(
	trail: { name: string; url: string }[],
): Record<string, unknown> | null {
	if (trail.length === 0) return null;

	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: trail.map((item, index) => ({
			'@type': 'ListItem',
			position: index + 1,
			name: item.name,
			item: item.url,
		})),
	};
}
